import { type ComponentType, createElement, type FC } from "react";

/**
 * Like React.lazy, but starts loading immediately at module scope and renders
 * synchronously once ready — no Suspense suspension on first render when the
 * module is already loaded.
 *
 * Still throws a promise (triggering the nearest Suspense boundary) if the
 * component is navigated to before the load completes.
 */
export const lazyPreload = <P extends object>(
  factory: () => Promise<ComponentType<P>>
): FC<P> => {
  let component: ComponentType<P> | null = null;
  let error: unknown = null;

  const promise = factory()
    .then((loadedModule) => {
      component = loadedModule;
    })
    .catch((reason: unknown) => {
      error = reason;
    });

  const LazyWrapper: FC<P> = (props) => {
    if (error) {
      throw error;
    }

    if (!component) {
      throw promise;
    }

    return createElement(component, props);
  };

  return LazyWrapper;
};
